import React, { useEffect, useState } from "react";
import { FieldType } from "../../../services/helpers/types";
import UrineSubField from "../../atoms/UrineSubField/UrineSubField";
import { marks2, marks4 } from "./helpers";
import { Checkbox } from "@mui/material";
import { useTranslation } from "react-i18next";

interface UrineFieldProps {
  field: FieldType;
  value?: string;
  onChange: (value: string) => void;
}

interface UrineFieldValueProps {
  Leukocyty: string;
  Bielkoviny: string;
  Nitrity: string;
  Krv: string;
}

type AllowedStrings = "Leukocyty" | "Bielkoviny" | "Nitrity" | "Krv";

const UrineField = (props: UrineFieldProps) => {
  const { value, onChange } = props;

  const { t } = useTranslation();

  const [notDone, setNotDone] = useState(false);
  const [fieldValue, setFieldValue] = useState<UrineFieldValueProps>({
    Leukocyty: "-",
    Bielkoviny: "-",
    Nitrity: "-",
    Krv: "-",
  });

  useEffect(() => {
    if (value) {
      const newValue = value.split(", ");

      const newValueObject = newValue.reduce((acc, value) => {
        const [key, valueString] = value.split(": ");
        return {
          ...acc,
          [key]: valueString,
        };
      }, {} as UrineFieldValueProps);

      const keys = Object.keys(newValueObject);

      const isNegative =
        keys.includes(t("negative").toLocaleLowerCase()) ||
        keys.includes(t("negative"));
      const isNotDone =
        keys.includes(t("notDone").toLocaleLowerCase()) ||
        keys.includes(t("notDone"));

      if (isNotDone) {
        setNotDone(true);
      }

      if (isNegative || isNotDone) {
        setFieldValue({
          Leukocyty: "-",
          Bielkoviny: "-",
          Nitrity: "-",
          Krv: "-",
        });
      } else {
        setFieldValue(newValueObject);
      }
    }
  }, [t, value]);

  const handleFieldChange = (title: string, value: string) => {
    setNotDone(false);

    let newValue = fieldValue;

    switch (title) {
      case t("leukocytes"):
        newValue = { ...fieldValue, Leukocyty: value };
        break;
      case t("proteins"):
        newValue = { ...fieldValue, Bielkoviny: value };
        break;
      case t("nitrites"):
        newValue = { ...fieldValue, Nitrity: value };
        break;
      case t("blood"):
        newValue = { ...fieldValue, Krv: value };
        break;
    }

    setFieldValue(newValue);

    const isNegative = Object.values(newValue).every((value) => value === "-");

    if (isNegative) {
      onChange(t("negative").toLocaleLowerCase());
    } else {
      const keys = Object.keys(newValue) as AllowedStrings[];

      const newValueString = keys
        .map((key) => {
          const value = newValue[key];
          return `${key}: ${value}`;
        })
        .join(", ");

      onChange(newValueString);
    }
  };

  const handleNotDoneCheck = () => {
    setFieldValue({
      Leukocyty: "-",
      Bielkoviny: "-",
      Nitrity: "-",
      Krv: "-",
    });
    onChange(
      !notDone
        ? t("notDone").toLocaleLowerCase()
        : t("negative").toLocaleLowerCase(),
    );
    setNotDone(!notDone);
  };

  return (
    <div className="dui-urine-field">
      <div className="dui-urine-field-left">
        <Checkbox
          checked={notDone}
          onChange={handleNotDoneCheck}
          className="dui-urine-field-checkbox"
        />
        <div className="dui-urine-field-checkbox-text">{t("notDone")}</div>
      </div>
      <div className="dui-urine-field-right">
        <UrineSubField
          marks={marks4}
          title={t("leukocytes")}
          value={fieldValue.Leukocyty}
          onChange={handleFieldChange}
        />
        <UrineSubField
          marks={marks4}
          title={t("proteins")}
          value={fieldValue.Bielkoviny}
          onChange={handleFieldChange}
        />
        <UrineSubField
          marks={marks2}
          title={t("nitrites")}
          value={fieldValue.Nitrity}
          onChange={handleFieldChange}
        />
        <UrineSubField
          marks={marks4}
          title={t("blood")}
          value={fieldValue.Krv}
          onChange={handleFieldChange}
        />
      </div>
    </div>
  );
};

export default UrineField;
